import {Injectable, OnDestroy, signal} from '@angular/core';
import {BehaviorSubject, firstValueFrom, Subject} from 'rxjs';
import {takeUntil} from 'rxjs/operators';
import {IQuickMessageCategory, IQuickMessageItem} from '../models/quick-message.model';
import {ConversationApiService} from '@shared/services/api/conversation/conversation-api.service';
import {AuthService} from '@core/authentication/auth.service';
import {IQmsgSearchSettings} from '../pipes/qmsg-filter.util';

/**
 * Quản lý danh sách Tin nhắn Nhanh (Quick Message).
 *
 * Flow:
 * 1. Click hội thoại → loadQuickMessage(fanpageId)
 * → check cache theo page hoặc group
 * → nếu chưa có → handleQuickMsgGroup xác định page thuộc group không
 * - Có group → loadByBusinessId (cache theo group)
 * - Không group → loadByPage (cache theo page)
 * 2. Lần click sau → dùng cache, không gọi API
 * 3. Thêm/Sửa/Xoá → DO event cập nhật cache (upsert/remove)
 */
@Injectable({providedIn: 'root'})
export class QuickMessageService implements OnDestroy {
  private _items$ = new BehaviorSubject<IQuickMessageItem[]>([]);
  public items$ = this._items$.asObservable();

  private _categories$ = new BehaviorSubject<IQuickMessageCategory[]>([]);
  public categories$ = this._categories$.asObservable();

  /** Cache pageId → items (FE13 cacheName: 'quickMsg_' + pageId) */
  private cacheByPage = new Map<string, IQuickMessageItem[]>();
  /** Cache groupId → items (FE13 cacheName: 'quickMsg_gr_' + groupId) */
  private cacheByGroup = new Map<string, IQuickMessageItem[]>();
  /** Cache businessId → categories */
  private categoryCacheByBusiness = new Map<string, IQuickMessageCategory[]>();
  /** GroupId của page hiện tại (nếu có) */
  private currentGroupId = '';
  /** Signal: page hiện tại có group không — dùng để UI quyết định auto-select page filter */
  public readonly hasGroup = signal(false);
  /** Set pageId đang load (guard race condition) — không gọi lại API khi đang load */
  private _loadingPages = new Set<string>();

  /** [FE13 settingQuickMsgMatch] Cài đặt tìm kiếm theo field */
  public searchSettings: IQmsgSearchSettings = {
    shortcut: true,
    title: true,
    message: true,
  };

  private readonly _destroy$ = new Subject<void>();

  constructor(
    private api: ConversationApiService,
    private authService: AuthService,
  ) {
    // Khi auth thay đổi (chọn page, đổi business) → xoá cache, lần sau load lại từ API
    this.authService.onAuthChanged$.pipe(takeUntil(this._destroy$)).subscribe(() => {
      this.resetState();
    });
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  public setItems(items: IQuickMessageItem[]): void {
    this._items$.next(items || []);
  }

  public setCategories(items: IQuickMessageCategory[]): void {
    this._categories$.next(items || []);
  }

  /**
   * Load quick messages cho 1 page. Cache theo page hoặc group.
   *
   * Flow:
   * 1. Cache by page → trả về ngay
   * 2. Cache by group → filter items match pageId → trả về
   * 3. Chưa có cache → handleQuickMsgGroup xác định page có trong group không
   * - Có group → loadByBusinessId (cache group)
   * - Không → loadByPage (cache page)
   */
  public async loadQuickMessage(pageId: string, forceReload = false): Promise<IQuickMessageItem[]> {
    if (!pageId) return [];

    // Đang load page này rồi → tránh gọi lại (trừ khi force reload)
    if (!forceReload && this._loadingPages.has(pageId)) return [];

    // Reset hasGroup — sẽ set lại đúng dựa vào cache hoặc API response.
    // Tránh trường hợp cacheByPage hit sau một lần group trước đó.
    this.hasGroup.set(false);

    // Cache by page → trả về ngay
    if (!forceReload) {
      const cachedByPage = this.cacheByPage.get(pageId);
      if (cachedByPage) {
        this._items$.next([...cachedByPage]);
        return cachedByPage;
      }

      // Cache by group → filter items match pageId hoặc items không có pageId
      for (const [, items] of this.cacheByGroup) {
        if (items.length > 0) {
          const pageItems = items.filter(i => !i.pageId || i.pageId === pageId);
          this.hasGroup.set(true);
          this._items$.next(pageItems.length > 0 ? pageItems : items);
          return pageItems.length > 0 ? pageItems : items;
        }
      }
    }

    this._loadingPages.add(pageId);

    try {
      const businessId = (this.authService as any)?.businessCurrent?.id as string | undefined;

      if (businessId) {
        // FE13: handleQuickMsgGroup → kiểm tra page có trong group không
        try {
          const groupRes = await firstValueFrom(this.api.handleQuickMsgGroup({
            businessId,
            pageId,
            act: 'list',
          }));
          // API v2 handlegroup act=list trả về data.result
          const groupItems: any[] = (groupRes as any)?.data?.result ?? [];
          if (groupItems.length > 0) {
            this.currentGroupId = String(groupItems[0].id || '');
            this.hasGroup.set(true);
            void this.loadCategories(String(businessId), forceReload);
            // Xoá cache page-level để tránh loadQuickMessage lần sau
            // tìm thấy dữ liệu page-level cũ (do loadByPage lưu) thay vì group items.
            // FE13: group items là nguồn chính xác duy nhất cho page có group.
            this.cacheByPage.delete(pageId);
            return this.loadByBusinessId(String(businessId), this.currentGroupId, forceReload);
          }
        } catch { /* fallback → load theo page */
        }
      }

      // Không có group → load by page
      this.hasGroup.set(false);
      return this.loadByPage(pageId, forceReload);
    } finally {
      this._loadingPages.delete(pageId);
    }
  }

  /**
   * Load items theo business+groupId. Cache theo group để tái sử dụng
   * cho tất cả page trong cùng group.
   */
  private async loadByBusinessId(
    businessId: string,
    groupId: string,
    forceReload = false,
  ): Promise<IQuickMessageItem[]> {
    const cacheKey = 'gr_' + groupId;

    if (!forceReload && this.cacheByGroup.has(cacheKey)) {
      const cached = this.cacheByGroup.get(cacheKey) || [];
      this._items$.next([...cached]);
      return cached;
    }

    try {
      const res = await firstValueFrom(this.api.getQuickMsgByBusinessId({
        businessId,
        groupId,
        status: 1,
      }));
      const rawItems: any[] = (res as any)?.items ?? (res as any)?.data?.items ?? [];
      const processed = this.processRawItems(rawItems);

      this.cacheByGroup.set(cacheKey, processed);
      this._items$.next(processed);
      return processed;
    } catch {
      return [];
    }
  }

  /**
   * Load items theo pageId. Dùng API /business/quickmessage/list với pageIds.
   * Cache theo page để tái sử dụng trong cùng session.
   */
  public async loadByPage(pageId: string, forceReload = false): Promise<IQuickMessageItem[]> {
    if (!forceReload && this.cacheByPage.has(pageId)) {
      const cached = this.cacheByPage.get(pageId) || [];
      this._items$.next([...cached]);
      return cached;
    }

    try {
      const res = await firstValueFrom(this.api.getQuickMessagesByPageIds([pageId]));
      // API v2 quickmessage/list trả về data.result
      const rawItems: any[] = (res as any)?.data?.result ?? [];
      const processed = this.processRawItems(rawItems);

      this.cacheByPage.set(pageId, processed);
      this._items$.next(processed);
      return processed;
    } catch {
      return [];
    }
  }

  /**
   * [FE13 processRawItems] Xử lý dữ liệu thô từ API giống FE13:
   * - Thêm '/' vào shortcut nếu chưa có
   * - Tự động tạo shortcut /(index+1) nếu item không có phím tắt (FE13 default behavior)
   * - Tách ats → image/video
   */
  private processRawItems(rawList: any[]): IQuickMessageItem[] {
    return rawList.map((r: any, index: number) => {
      if (Object.prototype.hasOwnProperty.call(r, 'shortcut') && r.shortcut && !String(r.shortcut).startsWith('/')) {
        r.shortcut = '/' + r.shortcut;
      } else if (!Object.prototype.hasOwnProperty.call(r, 'shortcut') || !r.shortcut) {
        // FE13: auto-generate shortcut theo số thứ tự
        r.shortcut = '/' + (index + 1);
      }

      if (Object.prototype.hasOwnProperty.call(r, 'ats') && Array.isArray(r.ats)) {
        r.ats.forEach((v: any) => (v.idQms = r.id));
      }

      return {
        id: String(r.id || ''),
        shortcut: String(r.shortcut || ''),
        title: String(r.title || ''),
        message: String(r.message || ''),
        backgroundColor: String(r.backgroundColor || r.bgColor || ''),
        color: String(r.color || ''),
        ats: (r.ats as unknown[]) || [],
        image: (r.image as unknown[]) || (Array.isArray(r.ats) ? r.ats.filter((i: any) => !i.type || i.type === 'image') : []),
        video: (r.video as unknown[]) || (Array.isArray(r.ats) ? r.ats.filter((i: any) => i.type === 'video') : []),
        pageId: String(r.pageId || r.fanpageId || ''),
        categoryId: r.categoryId ? String(r.categoryId) : undefined,
        childs: (r.childs as any[]) || undefined,
        requireAddress: r.requireAddress ? Number(r.requireAddress) : undefined,
        sendPriority: r.sendPriority ? String(r.sendPriority) : undefined,
        templates: Array.isArray(r.templates) ? r.templates : undefined,
        formId: r.formId ? String(r.formId) : undefined,
      };
    });
  }

  /**
   * [FE13 loadQuickMsgCategories] Load categories.
   */
  public async loadCategories(businessId: string, forceReload = false): Promise<IQuickMessageCategory[]> {
    if (!businessId) return [];

    if (!forceReload && this.categoryCacheByBusiness.has(businessId)) {
      const cached = this.categoryCacheByBusiness.get(businessId) || [];
      this._categories$.next(cached);
      return cached;
    }

    try {
      const res = await firstValueFrom(this.api.getQuickMessageCategories({
        businessId,
        act: 'list',
      }));
      const rawItems: any[] = (res as any)?.data?.result ?? [];
      const fetched: IQuickMessageCategory[] = rawItems.map((entry, index) => ({
        id: String(entry.id || ''),
        name: String(entry.name || ''),
        order: Number(entry.order || index),
      }));
      this.categoryCacheByBusiness.set(businessId, fetched);
      this._categories$.next(fetched);
      return fetched;
    } catch {
      return [];
    }
  }

  // ── DO Event handlers (giống TagService.upsertTag / removeTags) ───────────

  /**
   * Cập nhật hoặc thêm mới 1 quick message trong cache khi nhận DO event.
   * Pattern giống FE13 handleQuickMessageChanged() — xử lý cả action (replace/add) và individual upsert.
   *
   * @param data payload từ DO event: { type, value, idsRemove, action, messages }
   */
  public handleQuickMessageChanged(data: any): void {
    if (!data || data.type !== 'Qmsg') return;

    const pageId = data.value?.fanpageId;
    const groupId = data.groupId;

    // ── Xử lý xoá ──────────────────────────────────────────────────────
    if (data.idsRemove) {
      this.removeFromCache(pageId, groupId, data.idsRemove);
    }

    // ── Xử lý action (1=replace, 2=add) ────────────────────────────────
    if (data.action) {
      if (pageId) {
        this.applyActionToCache(pageId, this.cacheByPage, data);
      }
      if (groupId) {
        this.applyActionToCache('gr_' + groupId, this.cacheByGroup, data);
      }
    }

    // ── Xử lý individual upsert ────────────────────────────────────────
    if (!data.idsRemove && !data.action && data.value) {
      this.upsertSingleItem(pageId, groupId, data.value);
    }

    // Cập nhật _items$ nếu đang xem page/group liên quan
    this.syncCurrentItems(pageId);
  }

  private removeFromCache(pageId: string | undefined, groupId: string | undefined, idsRemove: string[]): void {
    if (pageId) {
      const cached = this.cacheByPage.get(pageId);
      if (cached) {
        this.cacheByPage.set(pageId, cached.filter((i: any) => !idsRemove.includes(i.id)));
      }
    }
    if (groupId) {
      const cacheKey = 'gr_' + groupId;
      const cached = this.cacheByGroup.get(cacheKey);
      if (cached) {
        this.cacheByGroup.set(cacheKey, cached.filter((i: any) => !idsRemove.includes(i.id)));
      }
    }
  }

  private applyActionToCache(
    key: string,
    cache: Map<string, IQuickMessageItem[]>,
    data: any,
  ): void {
    if (!cache.has(key)) return;

    if (data.action === 1) {
      // Replace all
      cache.set(key, []);
    }

    // Add messages
    const existing = cache.get(key) || [];
    for (const msg of data.messages || []) {
      if (msg.shortcut && !String(msg.shortcut).startsWith('/')) {
        msg.shortcut = '/' + msg.shortcut;
      }
      existing.push(this.processRawItems([msg])[0] || msg);
    }
    cache.set(key, existing);
  }

  private upsertSingleItem(pageId: string | undefined, groupId: string | undefined, value: any): void {
    const item = value;

    // Xác định cache cần upsert
    const updateCache = (cache: Map<string, IQuickMessageItem[]>, key: string) => {
      if (!cache.has(key)) return;
      const list = cache.get(key) || [];
      const idx = list.findIndex(i => i.id === String(item.id));
      if (idx > -1) {
        // Update existing
        const existing = list[idx];
        existing.order = Number(item.order ?? existing.order);
        if (item.shortcut) existing.shortcut = '/' + String(item.shortcut).replace(/^\//, '');
        if (item.fanpageId) existing.pageId = String(item.fanpageId);
        if (item.color) existing.color = String(item.color);
        if (item.backgroundColor) existing.backgroundColor = String(item.backgroundColor);
        if (item.title) existing.title = String(item.title);
        if (item.message) existing.message = String(item.message);
        if (item.ats) {
          existing.ats = item.ats;
          for (const atm of existing.ats!) atm.idQms = existing.id;
        } else {
          delete existing.ats;
        }
        if (item.childs) {
          existing.childs = item.childs;
        } else {
          delete existing.childs;
        }
        existing.requireAddress = item.requireAddress ? Number(item.requireAddress) : undefined;
        existing.sendPriority = item.sendPriority ? String(item.sendPriority) : undefined;
        cache.set(key, [...list]);
      } else {
        // Insert new
        if (item.shortcut && !String(item.shortcut).startsWith('/')) {
          item.shortcut = '/' + item.shortcut;
        }
        list.push(this.processRawItems([item])[0] || item);
        cache.set(key, [...list]);
      }
    };

    if (pageId && this.cacheByPage.has(pageId)) {
      updateCache(this.cacheByPage, pageId);
    }
    if (groupId) {
      const cacheKey = 'gr_' + groupId;
      if (this.cacheByGroup.has(cacheKey)) {
        updateCache(this.cacheByGroup, cacheKey);
      }
    }
  }

  private syncCurrentItems(pageId: string | undefined): void {
    const currentItems = this._items$.getValue();
    if (!currentItems.length || !pageId) return;

    // Tìm items từ cache tương ứng với pageId hiện tại
    const byPage = this.cacheByPage.get(pageId);
    if (byPage) {
      this._items$.next([...byPage]);
      return;
    }
    for (const [, items] of this.cacheByGroup) {
      const matched = items.filter(i => i.pageId === pageId);
      if (matched.length > 0) {
        this._items$.next(matched);
        return;
      }
    }
  }

  // ── Reset state (giống TagService.resetState) ───────────────────────────

  /**
   * Reset state — gọi khi đổi business hoặc logout.
   */
  public resetState(): void {
    this._items$.next([]);
    this._categories$.next([]);
    this.cacheByPage.clear();
    this.cacheByGroup.clear();
    this.categoryCacheByBusiness.clear();
    this.currentGroupId = '';
    this._loadingPages.clear();
    this.hasGroup.set(false);
  }

  // ── Search Settings (FE13 settingQuickMsgMatch) ──────────────────────────

  /**
   * [FE13 getSettingUserQuickMsg] Load cài đặt tìm kiếm từ server.
   */
  public async loadSearchSettings(): Promise<void> {
    try {
      const res: any = await firstValueFrom(this.api.getUserSetting());
      const setting = res?.setting ?? res?.data?.setting;
      if (setting?.searchQmsgMatch) {
        this.searchSettings.shortcut = setting.searchQmsgMatch.shortcut == '1' || setting.searchQmsgMatch.shortcut === true;
        this.searchSettings.title = setting.searchQmsgMatch.title == '1' || setting.searchQmsgMatch.title === true;
        this.searchSettings.message = setting.searchQmsgMatch.message == '1' || setting.searchQmsgMatch.message === true;
      }
    } catch {
      // giữ nguyên default
    }
  }

  /**
   * [FE13 handleChangeSettingSearchQmsg] Cập nhật + lưu setting.
   */
  public async updateSearchSetting(field: 'shortcut' | 'title' | 'message', checked: boolean): Promise<void> {
    this.searchSettings[field] = checked;
    const params = {
      qmsg: JSON.stringify({
        shortcut: this.searchSettings.shortcut ? 1 : 0,
        title: this.searchSettings.title ? 1 : 0,
        message: this.searchSettings.message ? 1 : 0,
      }),
    };
    try {
      await firstValueFrom(this.api.updateSettingQmsg(params));
    } catch {
      // silent
    }
  }

  /** Get tất cả items hiện tại (cho parent component filter) */
  get currentItems(): IQuickMessageItem[] {
    return this._items$.getValue();
  }

  /** [FE13] Lọc theo shortcut */
  public filterByShortcut(keyword: string, pageId?: string): IQuickMessageItem[] {
    const list = this._items$.getValue();
    const normalized = (keyword || '').replace(/^\//, '').toLowerCase().trim();

    return list.filter(item => {
      const passPage = !pageId || item.pageId === pageId;
      const shortcut = (item.shortcut || '').replace(/^\//, '').toLowerCase();
      return passPage && shortcut.includes(normalized);
    });
  }

  /** [FE13] Tìm kiếm theo message/title/shortcut */
  public search(keyword: string, pageId?: string): IQuickMessageItem[] {
    const list = this._items$.getValue();
    const k = (keyword || '').toLowerCase();

    return list.filter(i => {
      const okPage = !pageId || i.pageId === pageId;
      const text = (i.message || '') + ' ' + (i.title || '') + ' ' + (i.shortcut || '');
      return okPage && text.toLowerCase().includes(k);
    });
  }
}
